import { useEffect, useRef, useState } from 'react';
import { Fab, IconButton, useTheme, Box } from '@mui/material';
import { useHistory } from 'react-router-dom';

import useMenuAreas from 'modules/common/components/Layout/Sidebar/useMenuAreas';
import Item from 'modules/common/components/Layout/Sidebar/Item';
import SidebarList from 'modules/common/components/Layout/Sidebar/common/SidebarList';
import DrawerHeader from 'modules/common/components/Layout/Sidebar/common/DrawerHeader';
import Drawer, { LEFT_DRAWER_COLLAPSED_WIDTH } from 'modules/common/components/Layout/Sidebar/common/Drawer';
import useThemeBreakpoint from 'modules/common/useThemeBreakpoint';
import CollectiveIcon from 'modules/common/components/Icons/CollectiveIcon';
import { useLayoutContext } from 'modules/common/components/Layout/LayoutContext';
import useSubMenu from 'modules/common/components/Layout/Sidebar/common/useSubMenu';
import MinimizeExpand from 'modules/common/components/Icons/MinimizeExpand';
import ChevronRightIcon from 'modules/common/components/Icons/ChevronRightIcon';
import { useMemberPaymentContext } from 'modules/common/components/Payment/MemberPaymentContext';

export default function Sidebar() {
    const { isSidebarHidden, setIsSidebarHidden } = useLayoutContext();
    const theme = useTheme();
    const history = useHistory();

    const timmerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const breakpoint = useThemeBreakpoint();

    const [drawerVariant, setDrawerVariant] = useState<'temporary' | 'permanent'>(
        isSidebarHidden ? 'temporary' : 'permanent'
    );
    const menuAreas = useMenuAreas();
    const { isPaymentPending } = useMemberPaymentContext();

    const hideSidebar = () => {
        setIsSidebarHidden(true);
    };

    const openSidebar = () => {
        setIsSidebarHidden(false);
    };

    const navigateToDashboard = () => {
        history.push('/dashboard');
    };

    const [subMenuSelected, setSubMenuSelected] = useSubMenu();

    useEffect(() => {
        const cleanup = () => {
            if (timmerRef.current) {
                clearTimeout(timmerRef.current);
            }
        };

        /**
         * Drawer works differently from temporary to permanent as
         * temporary removes the entire element from the DOM, so
         * we switch back and forth the variants on medium size screens
         * to have an overlay on expand and keep the drawer visible
         * on collapse.
         */
        if (isSidebarHidden) {
            if (subMenuSelected !== null) {
                setSubMenuSelected(null);
            }
            timmerRef.current = setTimeout(() => {
                setDrawerVariant('permanent');
            }, theme.transitions.duration.leavingScreen - 100);
        } else if (breakpoint !== 'large') {
            setDrawerVariant('temporary');
        }

        return cleanup;
    }, [isSidebarHidden]);

    const shouldApplyMinWidth = breakpoint !== 'large' && (!isSidebarHidden || drawerVariant === 'temporary');

    return (
        <Box sx={shouldApplyMinWidth ? { minWidth: LEFT_DRAWER_COLLAPSED_WIDTH } : {}}>
            <Drawer
                ModalProps={{ disableScrollLock: true }}
                elevation={0}
                anchor="left"
                aria-label="sidebar"
                variant={drawerVariant}
                open={!isSidebarHidden}
                onClose={hideSidebar}
                PaperProps={{
                    sx: {
                        boxShadow: 'none',
                        paddingBottom: 2,
                        paddingRight: 2,
                        paddingLeft: 2,
                        borderRadius: 0,
                        backgroundColor: 'cement.50',
                        overflowX: 'hidden',
                        ...(isPaymentPending && {
                            zIndex: ({ zIndex }) => zIndex.drawer - 1,
                        }),
                    },
                }}>
                <DrawerHeader
                    sx={{
                        width: 1,
                        justifyContent: 'flex-start',
                        minHeight: 'auto',
                        paddingTop: 2,
                        paddingBottom: 1,
                    }}>
                    <CollectiveIcon sx={{ fontSize: '2.75rem', cursor: 'pointer' }} onClick={navigateToDashboard} />
                    {!isSidebarHidden && (
                        <IconButton onClick={hideSidebar} sx={{ marginLeft: 'auto' }} aria-label="collapse sidebar">
                            <MinimizeExpand fontSize="small" sx={{ color: 'cement.700' }} />
                        </IconButton>
                    )}
                </DrawerHeader>
                <SidebarList>
                    {menuAreas.map((item) => (
                        <Item
                            subMenuSelected={subMenuSelected}
                            setSubMenuSelected={setSubMenuSelected}
                            isSidebarCollapsed={isSidebarHidden}
                            {...item}
                            key={item.name}
                        />
                    ))}
                </SidebarList>
            </Drawer>

            {isSidebarHidden && (
                <Fab
                    disableRipple
                    size="small"
                    sx={{
                        backgroundColor: 'white',
                        position: 'fixed',
                        zIndex: ({ zIndex }) => zIndex.drawer + 1,
                        top: ({ spacing }) => spacing(7),
                        left: ({ spacing }) => spacing(7.5),
                        width: 24,
                        height: 24,
                        minHeight: 24,
                    }}
                    onClick={openSidebar}
                    aria-label="expand sidebar">
                    <ChevronRightIcon sx={{ color: 'cement.500', fontSize: '1rem' }} />
                </Fab>
            )}
        </Box>
    );
}
